import * as React from 'react'
import { AlertTriangle } from 'lucide-react'

import type { PaymentSchedule } from '@/gen/cortado/v1/invoices_pb'
import { InvoicePaymentScheduleAdjustmentStrategyEnum } from '@/gen/cortado/v1/settings_pb'
import { formatCurrency } from '@/lib/currency-utils'
import {
  appendInvoicePaymentScheduleForTotal,
  getInvoicePaymentScheduleLockReason,
  invoicePaymentSchedulesToFormValues,
  rebalanceInvoicePaymentSchedulesForTotal,
  type InvoicePaymentScheduleFormValue,
} from '@/lib/payment-schedule-utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface PaymentScheduleAdjustmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  existingSchedules: PaymentSchedule[]
  previousTotalCents: number
  newTotalCents: number
  onSelect: (strategy: InvoicePaymentScheduleAdjustmentStrategyEnum) => void
  onCancel: () => void
}

// A schedule is editable for adjustment purposes exactly when it has no lock
// reason - a paid schedule always has a lock reason, so this also covers the
// "not paid" condition.
function isScheduleEditable(schedule: PaymentSchedule): boolean {
  return !getInvoicePaymentScheduleLockReason(schedule)
}

// Finds the single row that changed between two schedule lists and describes
// it as either a modified "last payment" row or a newly appended row.
function summarizeSingleChange(
  before: InvoicePaymentScheduleFormValue[],
  after: InvoicePaymentScheduleFormValue[],
): string {
  const beforeIds = new Set(before.filter((s) => s.id).map((s) => s.id))
  const added = after.find((s) => !s.id || !beforeIds.has(s.id))
  if (added) {
    return `New payment item of $${formatCurrency(added.amountCents || 0)}`
  }

  for (const b of before) {
    if (!b.id) continue
    const a = after.find((s) => s.id === b.id)
    if (a && (a.amountCents || 0) !== (b.amountCents || 0)) {
      return `Last payment: $${formatCurrency(b.amountCents || 0)} -> $${formatCurrency(a.amountCents || 0)}`
    }
  }

  return ''
}

// Describes every row that changed or was removed - used for the reduction
// case, which can ripple across more than one payment item.
function summarizeReduction(
  before: InvoicePaymentScheduleFormValue[],
  after: InvoicePaymentScheduleFormValue[],
): string[] {
  const afterById = new Map(after.filter((s) => s.id).map((s) => [s.id as string, s]))
  const lines: string[] = []

  for (const b of before) {
    if (!b.id) continue
    const a = afterById.get(b.id)
    if (!a) {
      lines.push(`${b.description || 'Payment'}: $${formatCurrency(b.amountCents || 0)} removed`)
      continue
    }
    if ((a.amountCents || 0) !== (b.amountCents || 0)) {
      lines.push(`${b.description || 'Payment'}: $${formatCurrency(b.amountCents || 0)} -> $${formatCurrency(a.amountCents || 0)}`)
    }
  }

  return lines
}

/**
 * Shown at save time when line-item edits changed a partially-paid invoice's
 * total. Lets the user choose how to apply the difference to the payment
 * schedule instead of dead-ending on a disabled Save button.
 */
export function PaymentScheduleAdjustmentDialog({
  open,
  onOpenChange,
  existingSchedules,
  previousTotalCents,
  newTotalCents,
  onSelect,
  onCancel,
}: PaymentScheduleAdjustmentDialogProps) {
  const amountPaidCents = existingSchedules.reduce(
    (sum, schedule) => sum + Number(schedule.amountPaidCents || 0n),
    0,
  )
  // Matches the floor enforced by rebalanceInvoicePaymentSchedulesForTotal: any
  // schedule with a lock reason (paid, has an active checkout session, or a
  // non-editable status) can't be reduced, so its amount is a hard floor -
  // not just the amount actually collected so far.
  const lockedTotalCents = existingSchedules.reduce((sum, schedule) => {
    return getInvoicePaymentScheduleLockReason(schedule) ? sum + Number(schedule.amountCents || 0n) : sum
  }, 0)
  const deltaCents = newTotalCents - previousTotalCents
  const currentFormSchedules = React.useMemo(
    () => invoicePaymentSchedulesToFormValues(existingSchedules),
    [existingSchedules],
  )

  const handleCancel = () => {
    onCancel()
    onOpenChange(false)
  }

  let body: React.ReactNode
  let footer: React.ReactNode = (
    <Button type="button" variant="outline" onClick={handleCancel}>
      Cancel
    </Button>
  )

  if (deltaCents > 0) {
    const hasEditableSchedule = existingSchedules.some(isScheduleEditable)

    if (hasEditableSchedule) {
      const lastPaymentResult = rebalanceInvoicePaymentSchedulesForTotal(
        existingSchedules,
        currentFormSchedules,
        newTotalCents,
      )
      const newPaymentResult = appendInvoicePaymentScheduleForTotal(currentFormSchedules, newTotalCents)
      const lastPaymentPreview = summarizeSingleChange(currentFormSchedules, lastPaymentResult)
      const newPaymentPreview = summarizeSingleChange(currentFormSchedules, newPaymentResult)

      body = (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => onSelect(InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_LAST_PAYMENT)}
            className="w-full rounded-lg border-2 border-border/60 p-4 text-left transition-colors hover:border-primary/50 hover:bg-accent/30"
          >
            <p className="font-medium">Add to last payment</p>
            {lastPaymentPreview && (
              <p className="mt-1 text-sm text-muted-foreground">{lastPaymentPreview}</p>
            )}
          </button>
          <button
            type="button"
            onClick={() => onSelect(InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_NEW_PAYMENT)}
            className="w-full rounded-lg border-2 border-border/60 p-4 text-left transition-colors hover:border-primary/50 hover:bg-accent/30"
          >
            <p className="font-medium">Add as new payment item</p>
            {newPaymentPreview && (
              <p className="mt-1 text-sm text-muted-foreground">{newPaymentPreview}</p>
            )}
          </button>
        </div>
      )
      footer = (
        <Button type="button" variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
      )
    } else {
      const newPaymentResult = appendInvoicePaymentScheduleForTotal(currentFormSchedules, newTotalCents)
      const preview = summarizeSingleChange(currentFormSchedules, newPaymentResult)

      body = (
        <div className="rounded-lg border bg-muted/50 p-4 text-sm">
          <p>All existing payment items are locked or paid. A new payment item will be added for the difference.</p>
          {preview && <p className="mt-1 font-medium">{preview}</p>}
        </div>
      )
      footer = (
        <>
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => onSelect(InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_NEW_PAYMENT)}
          >
            Add New Payment Item
          </Button>
        </>
      )
    }
  } else if (deltaCents < 0) {
    if (newTotalCents < lockedTotalCents) {
      body = (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <p>
            The new total (${formatCurrency(newTotalCents)}) is less than the ${formatCurrency(lockedTotalCents)} that
            can&apos;t be reduced (already collected, or locked by a pending checkout). This reduction can&apos;t be
            applied automatically - adjust the payment schedule manually on the Payments tab instead.
          </p>
        </div>
      )
    } else {
      const rebalanceResult = rebalanceInvoicePaymentSchedulesForTotal(
        existingSchedules,
        currentFormSchedules,
        newTotalCents,
      )
      const changeLines = summarizeReduction(currentFormSchedules, rebalanceResult)

      body = (
        <div className="space-y-1 rounded-lg border bg-muted/50 p-4 text-sm">
          <p className="font-medium">
            The last editable payment{changeLines.length > 1 ? 's' : ''} will be reduced by ${formatCurrency(-deltaCents)}.
          </p>
          {changeLines.map((line) => (
            <p key={line} className="text-muted-foreground">{line}</p>
          ))}
        </div>
      )
      footer = (
        <>
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => onSelect(InvoicePaymentScheduleAdjustmentStrategyEnum.INVOICE_PAYMENT_SCHEDULE_ADJUSTMENT_STRATEGY_LAST_PAYMENT)}
          >
            Apply Reduction
          </Button>
        </>
      )
    }
  } else {
    body = (
      <div className="rounded-lg border bg-muted/50 p-4 text-sm text-muted-foreground">
        The payment schedule doesn&apos;t match the invoice total. Open the Payments tab to adjust it manually.
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(next) : handleCancel())}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Update the payment schedule</DialogTitle>
          <DialogDescription>
            The invoice total changed. Choose how to apply the difference to the payment schedule.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 rounded-lg border bg-muted/30 p-3 text-sm">
          <div>
            <p className="text-muted-foreground">Previous total</p>
            <p className="font-financial font-medium">${formatCurrency(previousTotalCents)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">New total</p>
            <p className="font-financial font-medium">${formatCurrency(newTotalCents)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Already paid</p>
            <p className="font-financial font-medium">${formatCurrency(amountPaidCents)}</p>
          </div>
        </div>

        {body}

        <DialogFooter className="gap-2 sm:gap-0">
          {footer}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
